import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.05, ease: "easeOut" } }),
};

function GoogleG({ className }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function ReviewCard({ r, i }) {
  return (
    <motion.div custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
      data-testid={`review-card-${r.id}`}
      className="bg-white rounded-2xl border border-[#E8E2D9] p-6 shadow-[0_8px_30px_rgb(74,63,63,0.04)] flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-brand-light text-brand flex items-center justify-center font-display text-lg shrink-0">
          {r.author?.charAt(0)?.toUpperCase() || "?"}
        </div>
        <div className="min-w-0">
          <div className="font-medium truncate">{r.author}</div>
          <div className="text-xs text-ink-muted flex items-center gap-1">
            <GoogleG className="w-3.5 h-3.5" /> {r.source || "Google"}{r.date_label ? ` · ${r.date_label}` : ""}
          </div>
        </div>
      </div>
      <div className="flex gap-0.5 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-[#E8E2D9]"}`} strokeWidth={1.5} />
        ))}
      </div>
      {r.text && <p className="text-sm text-ink-muted leading-relaxed">{r.text}</p>}
    </motion.div>
  );
}

export default function ReviewsPage() {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });

  useEffect(() => {
    api.get("/public/reviews").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-cream text-ink font-sans">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-cream/80 border-b border-[#E8E2D9]">
        <div className="max-w-6xl mx-auto px-5 h-20 flex items-center justify-between">
          <a href="/" data-testid="reviews-home-link" className="flex items-center gap-2.5 font-display text-lg text-ink">
            <img src="/logo-round.png" alt="Nelleke Holt" className="w-9 h-9" />
            <span>Nelleke Holt</span>
          </a>
          <a href="/" className="text-sm text-ink-muted hover:text-brand transition-colors flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Terug naar home
          </a>
        </div>
      </header>

      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-brand">Wat klanten zeggen</span>
            <h1 className="mt-3 text-4xl sm:text-5xl font-medium tracking-tight">Reviews</h1>
            {data.count > 0 && (
              <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-full border border-[#E8E2D9] px-6 py-3 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
                <GoogleG className="w-6 h-6" />
                <span className="text-2xl font-medium">{data.average.toFixed(1).replace(".", ",")}</span>
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= Math.round(data.average) ? "fill-amber-400 text-amber-400" : "text-[#E8E2D9]"}`} strokeWidth={1.5} />
                  ))}
                </span>
                <span className="text-sm text-ink-muted">· Beoordeeld door tevreden klanten</span>
              </div>
            )}
          </div>

          {data.count === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E8E2D9] p-10 text-center text-ink-muted" data-testid="reviews-empty">
              Er zijn nog geen reviews om te tonen.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.reviews.map((r, i) => <ReviewCard key={r.id} r={r} i={i} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
